import type {ContentKey} from '../types';
import type {TranslateOptions, TranslationFn} from '../utils/types';
import {useTranslation} from './TranslationProvider';

type TranslationProps = {
  /**
   * The `contentKey` to use for the translation
   */
  contentKey?: ContentKey;
  /**
   * The `TranslateOptions` object to pass with the translation
   */
  options?: TranslateOptions;
  /**
   * Using the `children` render prop will return the underlying `t` function.
   * When using this method, the `contentKey` and `options` props do nothing.
   */
  children?: (t: TranslationFn<ContentKey>) => any;
};

/**
 * Use this component when translating keys in class-based React components.
 *
 * Usage with a `contentKey` prop and an optional `options` object:
 *
 *```tsx
 * import {Translation} from '@packages/systems/designer-translations';
 *
 * <h1>
 *   <Translation contentKey="key.of.string" options={{someKey: someValue}} />
 * </h1>
 *```
 *
 * Usage with `children` render props to obtain the translation function:
 *```tsx
 * <Translation>{(t) => <h1>{t('key.of.string')}</h1>}</Translation>;
 *```
 */
export const Translation = (props: TranslationProps) => {
  const t = useTranslation();

  if (props.children) {
    // Children are only supported as a render function. If anything else is passed as children,
    // this is a noop and the children are simply returned with the `contentKey` prop ignored
    const {children} = props;
    return typeof children === 'function' ? children(t) : children;
  } else if (props.contentKey) {
    return t(props.contentKey, props.options);
  } else {
    return null;
  }
};
